import {Injectable} from "@angular/core";
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from "@angular/router";
import {Observable} from "rxjs";
import {filter, first, mergeMap, tap} from "rxjs/operators";
import {AppState} from "../../store/state/app.state";
import {Store} from "@ngrx/store";
import {NavigationService} from "../../navigation.service";
import {Order} from "../../store/state/order.state";
import {orderSelector} from "../../store/state/state.selectors";
import {OrderActions} from "../../store/actions/order.actions";
import {EndpointsProvider} from "../../umbrella-common/endpoints/endpoint-provider.service";

@Injectable()
export class ClientOrderResolver implements Resolve<Order> {

	private order$: Observable<Order>;

	constructor(private endpoints: EndpointsProvider, private store: Store<AppState>, private navigationService: NavigationService) {
		this.order$ = store.select(orderSelector);
	}

	resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<Order> {
		return this.order$.pipe(
			first(),
			mergeMap(order => {
				const forceRefresh = Boolean(route.paramMap.get('forceRefresh'));
				if (order != null && order.orderUuid != null && order.orderCorrelationId === route.paramMap.get('correlationId') && !order.takeoverThresholdExceeded && !forceRefresh) {
						this.navigateToProperOrderStepIfNeeded(order, route);

						return new Observable<Order>(subscriber => {
							subscriber.next(order);
							subscriber.complete();
						});
					} else {
						return this.fetchAndStore(route).pipe(tap(fetchedOrder => this.navigateToProperOrderStepIfNeeded(fetchedOrder, route, forceRefresh)));
					}
				}
			),
			filter(order => !order.takeoverThresholdExceeded || !this.isRouteUrlWrongForOrder(order, route))
		);
	}

	private navigateToProperOrderStepIfNeeded(order, route: ActivatedRouteSnapshot, forceRefresh?: boolean) {
		const preventRedirection: boolean = route.data['preventRedirection'];
		const routeUrlWrongForOrder = this.isRouteUrlWrongForOrder(order, route);
		if (!preventRedirection && routeUrlWrongForOrder) {
			this.navigationService.navigateToProperOrderStep(null, forceRefresh);
		}
	}

	private fetchAndStore(route: ActivatedRouteSnapshot): Observable<Order> {
		return this.fetchOrder(route).pipe(
			tap(o => this.store.dispatch(OrderActions.SET_ORDER({order: o})))
		);
	}

	private fetchOrder(route: ActivatedRouteSnapshot) {
		const correlationId: string = route.paramMap.get('correlationId');
		return this.endpoints.client.getOrder(correlationId);
	}

	private isRouteUrlWrongForOrder(order, route: ActivatedRouteSnapshot): boolean {
		const routeCommands: string[] = this.navigationService.getClientOrderNavigationCommand(order);
		return routeCommands[routeCommands.length - 1] !== route.url[route.url.length - 1].path;
	}
}
